/**
 * The clock the sheet editor's preview and the Pictures panel preview play
 * on (spec 035): while
 * `playing`, each animation frame reports `sheetFrameAt(cut, playback,
 * elapsed, spriteId)`.
 *
 * The clock keeps its progress (in 1× ms) across pauses and playback changes:
 * - Pause, then Play, resumes from the paused frame.
 * - A playback change while playing (speed, mode, range) takes effect on the
 *   next frame and keeps the phase: progress is carried over and read at the
 *   new speed, so the animation continues from the same point in its cycle.
 *   Switching to Once finishes the current cycle.
 * - A run that ends (Once, `done`) stops requesting frames and rewinds, so the
 *   next Play starts from the beginning.
 * - A new cut or sprite starts over.
 * `seek(index)` moves the clock to the start of a frame (a step or a strip
 * click), so Play continues from the frame shown. Pausing or unmounting
 * cancels the frame request.
 */
import { useCallback, useEffect, useRef } from 'react';
import type { SheetCut, StatePlayback } from '../../../../Shared/src/@types';
import { frameDurations, hashString, sheetFrameAt, sheetPlayLength } from '../../../../Shared/src/utils/spriteSheet';

/** How many 1× ms one ms at this playback's speed is (the clamped speed `sheetFrameAt` plays at). */
function speedRatio(cut: SheetCut, playback: StatePlayback | undefined): number {
  const one = frameDurations(cut, 1)[0];
  const at = frameDurations(cut, playback?.speed)[0];
  return one > 0 && at > 0 ? one / at : 1;
}

/** The played range, clamped into the cut (as `sheetFrameAt` plays it). */
function playedRange(cut: SheetCut, playback: StatePlayback | undefined, n: number): [number, number] {
  const r = playback?.range;
  if (!Array.isArray(r) || r.length !== 2) return [0, n - 1];
  const clamp = (v: number) => Math.min(n - 1, Math.max(0, Math.round(Number(v) || 0)));
  const a = clamp(r[0]);
  return [a, Math.max(a, clamp(r[1]))];
}

/** Progress (1× ms, before any random-start offset) at which frame `index` begins. */
function progressAt(cut: SheetCut, playback: StatePlayback | undefined, spriteId: string, index: number): number {
  const d = frameDurations(cut, 1);
  const [first, last] = playedRange(cut, playback, d.length);
  if (index < first || index > last) return 0;
  const sum = (a: number, b: number) => { let s = 0; for (let i = a; i < b; i++) s += d[i]; return s; };
  const start = sum(first, index);
  const mode = playback?.mode ?? 'loop';
  const offset = playback?.randomStart && mode !== 'still' ? sum(first, first + (hashString(String(spriteId ?? '')) % (last - first + 1))) : 0;
  if (mode === 'once') return Math.max(0, start - offset);
  const cycle = sum(first, last + 1) + (mode === 'pingpong' ? sum(first + 1, last) : 0);
  return cycle > 0 ? (((start - offset) % cycle) + cycle) % cycle : 0;
}

export function useSheetClock(
  cut: SheetCut | null | undefined,
  playback: StatePlayback | undefined,
  playing: boolean,
  spriteId: string,
  onFrame: (at: { index: number; done: boolean }) => void,
): { seek: (index: number) => void } {
  const report = useRef(onFrame);
  report.current = onFrame;
  const latest = useRef({ cut, playback });
  latest.current = { cut, playback };
  /** Played so far, in 1× ms. */
  const progress = useRef(0);
  const cutKey = JSON.stringify(cut ?? null);
  const playKey = JSON.stringify(playback ?? null);

  // A new sheet or sprite starts over (declared first, so it runs before the clock restarts).
  const started = useRef(false);
  useEffect(() => {
    if (started.current) progress.current = 0;
    started.current = true;
  }, [cutKey, spriteId]);

  useEffect(() => {
    if (!latest.current.cut || !playing || typeof requestAnimationFrame !== 'function') return undefined;
    const { cut: c, playback: p } = latest.current;
    const k = speedRatio(c!, p);
    // Switching to Once mid-loop finishes the current cycle rather than ending at once.
    if (p?.mode === 'once') {
      const cycle = sheetPlayLength(c!, { ...p, randomStart: false, speed: 1 }, spriteId);
      if (cycle > 0 && Number.isFinite(cycle)) progress.current %= cycle;
    }
    let live = true;
    let last: number | null = null;
    let raf = 0;
    const tick = (now: number) => {
      if (!live) return;
      if (last !== null) progress.current += Math.max(0, now - last) * k;
      last = now;
      const at = sheetFrameAt(c!, p, progress.current / k, spriteId);
      if (at.done) progress.current = 0;
      report.current(at);
      if (!at.done && live) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { live = false; cancelAnimationFrame(raf); };
  }, [cutKey, playKey, playing, spriteId]);

  const seek = useCallback((index: number) => {
    const { cut: c, playback: p } = latest.current;
    progress.current = c ? progressAt(c, p, spriteId, index) : 0;
  }, [spriteId]);
  return { seek };
}
